import {
  Boxes,
  Network,
  Bot,
  AppWindow,
  ListChecks,
  Target,
  Users,
  Building2,
  Waypoints,
} from "lucide-react";

const FRAMEWORKS = [
  "MITRE ATT&CK",
  "NIST CSF",
  "NICE Framework",
  "SCyWF",
  "NIST 800-181",
  "OWASP Top 10",
  "Purple Team",
  "Red Team",
  "Blue Team",
  "CTF",
  "SOC Analyst",
];

const FEATURES = [
  {
    icon: Network,
    tag: "// ranges",
    title: "On-Demand Cyber Range",
    body: "Provision isolated ranges in seconds on cloud infrastructure, connect over per-session VPN certificates, and auto-tear-down when the four-hour session ends.",
    wide: true,
    visual: "terminal",
  },
  {
    icon: Boxes,
    tag: "// catalog",
    title: "Scenario Catalog",
    body: "Browse a curated, role-filtered tree of challenges — CyberEdu learning paths, SCyWF job-role training, and emerging threats — or generate your own scenario with AI.",
  },
  {
    icon: AppWindow,
    tag: "// attack",
    title: "Browser Attack Boxes",
    body: "Launch a fully-tooled Kali attack box straight in your browser over Apache Guacamole — no VPN client, no local VM, no setup to babysit.",
  },
  {
    icon: Target,
    tag: "// scoring",
    title: "Framework Scoring",
    body: "Every completion maps to TKSA coverage and MITRE ATT&CK techniques, with alignment to the NICE / SCyWF workforce frameworks baked in.",
  },
  {
    icon: Waypoints,
    tag: "// ai",
    title: "AI Topology Designer",
    body: "Describe an IT or OT network in plain language and watch a validated, editable topology build itself on an interactive canvas — plan-first or fully autonomous.",
    wide: true,
    visual: "topology",
  },
  {
    icon: Users,
    tag: "// teams",
    title: "Team Challenges",
    body: "Run collaborative team labs on a shared VM where a correct answer locks the question team-wide, streamed live to every member in real time.",
  },
  {
    icon: ListChecks,
    tag: "// labs",
    title: "Hands-On Labs",
    body: "Guided questions with attempt limits, unlockable walkthroughs, response-and-remediation playbooks, and an embedded view of the scenario's own topology.",
  },
  {
    icon: Building2,
    tag: "// tenant",
    title: "Multi-Tenant & Role-Based",
    body: "Organizations, teams, and seven roles with per-feature permissions, plus dedicated sign-in portals for universities, enterprises, and individuals.",
  },
  {
    icon: Bot,
    tag: "// copilots",
    title: "AI Lab Assistants",
    body: "Four purpose-built copilots guide you without spoiling the answer:",
    wide: true,
    visual: "copilots",
  },
];

const COPILOTS = [
  "Scenario Advisor",
  "In-Box Assistant",
  "CTF-Agent",
  "Topology Architect",
];

function Marquee() {
  const items = [...FRAMEWORKS, ...FRAMEWORKS];
  return (
    <div className="lp-marquee-mask overflow-hidden">
      <div className="lp-marquee-track flex w-max items-center gap-3 hover:[animation-play-state:paused]">
        {items.map((name, i) => (
          <span
            key={i}
            className="qv-mono whitespace-nowrap rounded-lg border border-(--color-border) bg-(--color-bg-card) px-4 py-1.5 text-[11px] uppercase tracking-wider text-(--color-text-muted) transition-colors duration-200 hover:border-(--color-primary) hover:bg-(--color-primary-50) hover:text-(--color-primary)"
          >
            {name}
          </span>
        ))}
      </div>
    </div>
  );
}

function TileVisual({ kind }) {
  if (kind === "terminal") {
    return (
      <div className="lp-tile-visual qv-mono mt-5 rounded-lg border p-3 text-[12px] leading-[1.85]">
        <div>
          <span className="lp-term-accent">qv&gt;</span>{" "}
          <span className="text-(--color-text-secondary)">
            provision range --ttl 4h
          </span>
        </div>
        <div className="text-(--color-primary)">
          ✓ range up · vpn tunnel secured
        </div>
        <div className="text-(--color-primary)">
          ✓ auto-teardown armed
          <span className="ml-1 inline-block h-3 w-1.75 animate-pulse bg-(--color-primary) align-[-1px]" />
        </div>
      </div>
    );
  }
  if (kind === "topology") {
    return (
      <div className="lp-tile-visual mt-5 h-28 overflow-hidden rounded-lg border">
        <svg viewBox="0 0 380 112" className="h-full w-full">
          {[
            "M50,74 L140,42",
            "M140,42 L240,66",
            "M240,66 L320,34",
            "M140,42 L320,34",
          ].map((d) => (
            <path
              key={d}
              d={d}
              fill="none"
              stroke="var(--color-primary)"
              strokeOpacity="0.4"
              strokeWidth="1.4"
            />
          ))}
          {[
            [50, 74],
            [140, 42],
            [240, 66],
            [320, 34],
          ].map(([cx, cy]) => (
            <circle
              key={`${cx}-${cy}`}
              cx={cx}
              cy={cy}
              r="6"
              fill="var(--color-primary)"
              style={{ filter: "drop-shadow(0 0 5px var(--color-primary))" }}
            />
          ))}
          <circle r="2.4" className="lp-topo-pulse">
            <animateMotion
              dur="2.6s"
              repeatCount="indefinite"
              path="M50,74 L140,42"
            />
          </circle>
          <circle r="2.4" className="lp-topo-pulse">
            <animateMotion
              dur="3.1s"
              repeatCount="indefinite"
              path="M140,42 L320,34"
            />
          </circle>
        </svg>
      </div>
    );
  }
  if (kind === "copilots") {
    return (
      <div className="mt-4 flex flex-wrap gap-2">
        {COPILOTS.map((c) => (
          <span
            key={c}
            className="qv-mono rounded-md border border-(--color-border) px-2.5 py-1 text-[11px] text-(--color-primary)"
            style={{ background: "var(--color-primary-50)" }}
          >
            {c}
          </span>
        ))}
      </div>
    );
  }
  return null;
}

export default function Features() {
  return (
    <section id="features" className="relative py-16 lg:py-24">
      <div className="mx-auto max-w-350 px-5 md:px-10">
        {/* Framework trust strip */}
        <div className="lp-reveal">
          <p className="qv-mono text-center text-[11px] font-medium uppercase tracking-[0.24em] text-(--color-text-muted)">
            aligned with the frameworks security teams train on
          </p>
          <div className="mt-6">
            <Marquee />
          </div>
        </div>

        {/* Section heading */}
        <div className="lp-reveal mx-auto mt-24 max-w-2xl text-center">
          <div className="mb-4 flex items-center justify-center gap-2 qv-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-(--color-primary)">
            <span className="h-px w-6 bg-(--color-primary)" />
            Capabilities
            <span className="h-px w-6 bg-(--color-primary)" />
          </div>
          <h1
            className="lp-rise qv-glitch mt-6 text-[32px] sm:text-[40px] leading-tight font-normal tracking-tight text-(--color-text-primary)"
            style={{ "--lp-delay": "90ms" }}
          >
            {" "}
            Everything a cyber range needs, in{" "}
            <span className="lp-highlight">one platform</span>
          </h1>
        </div>

        {/* Subheading, styled as a full-width const declaration */}
        <p
          className="lp-reveal qv-mono mx-auto mt-8 max-w-5xl border-l-2 pl-4 text-left text-[13.5px] leading-relaxed"
          style={{
            borderColor:
              "color-mix(in srgb, var(--color-primary) 40%, transparent)",
          }}
        >
          <span className="font-medium" style={{ color: "#c084fc" }}>
            const
          </span>{" "}
          <span style={{ color: "#2dd4bf" }}>workflow</span>{" "}
          <span className="text-(--color-text-muted)">=</span>{" "}
          <span
            style={{
              color:
                "color-mix(in srgb, var(--color-primary) 38%, var(--color-text-secondary))",
            }}
          >
            "From the scenario catalog to live provisioning and AI-assisted
            topology design — the full offensive-and-defensive workflow, without
            the infrastructure overhead."
          </span>
          <span className="text-(--color-text-muted)">;</span>
        </p>

        {/* Bento grid */}
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.title}
                className={`lp-reveal ${feature.wide ? "lg:col-span-2" : ""}`}
                style={{ transitionDelay: `${(i % 4) * 70}ms` }}
              >
                <div className="qv-card qv-noise group relative flex h-full flex-col overflow-hidden p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-(--color-primary) hover:shadow-[0_24px_54px_-26px_rgba(0,0,0,0.7),0_0_42px_-22px_var(--color-shadow-primary)]">
                  {/* faint circuit grid, fading out of the top-right corner */}
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-40"
                    style={{
                      backgroundImage:
                        "linear-gradient(rgba(52,211,153,.045) 1px,transparent 1px),linear-gradient(90deg,rgba(52,211,153,.045) 1px,transparent 1px)",
                      backgroundSize: "26px 26px",
                      maskImage:
                        "radial-gradient(120% 120% at 100% 0%, #000, transparent 60%)",
                      WebkitMaskImage:
                        "radial-gradient(120% 120% at 100% 0%, #000, transparent 60%)",
                    }}
                  />

                  {/* corner bracket, drawn in on hover */}
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute right-3 top-3 h-3.5 w-3.5 border-r-2 border-t-2 opacity-0 transition-all duration-300 group-hover:right-2.5 group-hover:top-2.5 group-hover:h-5 group-hover:w-5 group-hover:opacity-100"
                    style={{ borderColor: "var(--color-primary)" }}
                  />

                  {/* mono micro-label */}
                  <span className="qv-mono absolute right-5 top-4 text-[11px] tracking-wide text-(--color-text-muted) transition-colors duration-300 group-hover:text-(--color-primary)">
                    {feature.tag}
                  </span>

                  <div className="relative z-10 flex h-full flex-col">
                    <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-(--color-border-solid) bg-(--color-primary-50) text-(--color-primary) transition-all duration-300 group-hover:bg-(--color-primary-100) group-hover:shadow-[0_0_22px_var(--color-shadow-primary)]">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-(--color-text-primary)">
                      <span className="-ml-3 text-(--color-primary) opacity-0 transition-all duration-300 group-hover:ml-0 group-hover:opacity-100">
                        ›
                      </span>
                      {feature.title}
                    </h3>
                    <p className="mt-2 text-[13.5px] leading-relaxed text-(--color-text-secondary)">
                      {feature.body}
                    </p>
                    {feature.visual && <TileVisual kind={feature.visual} />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
